import { Backdrop, Box, CircularProgress } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import { resetSubChartDetails } from "../../../store/Financials/saveChartSlice";
import { RootState } from "../../../store/store";
import basestyle from "../../CSS/Base.module.css";
import DownloadPopUpActuals from "../../PopUp/DownloadPopUpActuals";
import CancelPopUp from "../../PopUp/PopUp";
import { getBudgetActualDetailsChartAccountsByYearAndParticipantIdURL, getParticipantActualsStatusByParticipantIdAndFinancialYearAndMonth, postActualsChartAccountsTransactions, updateStatusAndActualSubmissionDateByParticipantIdAndFinancialYearAndMonth } from "../services/Financials/actuals.service";
import { getBudgetActualEligibleMonthsByYear, getBudgetForecastEligibleYearByParticipantIdAndYear } from "../services/Financials/budget.service";
import { ActualsChartIdReq, ForecastChartIdVO } from "../services/Financials/financials";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer, SubSectionTitle } from "../StyledComponents/Container";
import { formatCurrency, formatCurrencywithoutDoller } from "../Utils/formatUtils";
import { notifyUser } from "../Utils/notifyUser";
import ReusableChartsAccountTable, { Column } from "./ReusableChartsAccountTable";

const ActualsDetails = () => {
    const { state } = useLocation();
    const [chartAccountsVOList, setChartAccountsVOList] = useState<any[]>([]);
    const [eligibleMonthsVO, setEligibleMonthsVO] = useState([]);
    const [chartAssetAccountsList, setChartAssetAccountsList] = useState([]);
    const [chartNetAssetAccountsList, setChartNetAssetAccountsList] = useState([]);
    const [chartIncomeAccountsList, setChartIncomeAccountsList] = useState([]);
    const [chartExpensesAccountsList, setChartExpensesAccountsList] = useState([]);
    const [chartLiabilitiesAccountsList, setChartLiabilitiesAccountsList] = useState([]);
    const [totalIncome, setTotalIncome] = useState(0);
    const [tockenId, setTockenId] = useState(0);
    const [totalExpenses, settotalExpenses] = useState(0);
    const [totalNetAsset, settotalNetAsset] = useState(0);
    const [totalAsset, settotalAasset] = useState(0);
    const [totalLiabilities, settotalLiabilities] = useState(0);
    const [loadingFlag, setLoadingFlag] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [expandAll, setExpandAll] = useState(false);
    const [participantId, setParticipantId] = useState(0);
    const [currentYear, setCurrentYear] = useState(0);
    const forecastYear = state?.forecastYear ?? "";
    const [openPopUp, setOpenPopUp] = useState(false);
    const [readOnlyForecast, setReadOnlyForecast] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [markasReadFlag, setMarkasReadFlag] = useState(false);
    const [openDownloadPopUp, setOpenDownloadPopUp] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userDetails = useSelector((state: RootState) => state.auth);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const [saveUpdatedChartAccountsList, setSaveUpdatedChartAccountsList] = useState<any[]>([]);
    const participantProfile = useSelector((state: RootState) => state.participantAdminProfile);
    const subChartSaveVO = useSelector((state: RootState) => state.subChart);

    useEffect(() => {
        setParticipantId(userDetails.defaultParticipantId);
        const currentYear = parseInt(forecastYear.substring(2, 4)) + 2000;
        setCurrentYear(currentYear);
    }, [userDetails]);

    const columns: Column[] = [
        { id: "accountNumber", label: translate('chartAccountNo'), minWidth: 150, align: "center" },
        { id: "accountName", label: translate('description'), minWidth: 150, align: "left" },
        { id: "curActualAmount", label: translate('actualsAmount'), minWidth: 150, align: "right" },
        { id: "curBudget", label: translate('budgetAmount'), minWidth: 200, align: "right" },
        { id: "lastYearTotal", label: translate('lastYearYTDTotal'), minWidth: 200, align: "right" },
        { id: "incDec", label: translate('increase/Decr'), minWidth: 200, align: "right" },
        { id: "percentage", label: translate('%'), minWidth: 200, align: "center" },
    ];
    const filterColumns = (tableKey: string) => {
        if (tableKey === "chartAssetAccountsListing" || tableKey === "chartLiabilitiesAccountsListing" || tableKey === "chartNetAssetsAccountsListing") {
            return columns.filter(column => column.id !== "percentage" && column.id !== "curBudget" && column.id !== "incDec");
        } else if (tableKey === "chartActualsIncomeAccountsListing" || tableKey === "chartActualsExpensesAccountsListing") {
            return columns.filter(column => column.id !== "lastYearTotal");
        } else {
            return columns;
        }
    };

    useEffect(() => {
        if (participantId && forecastYear) {
            getBudgetActualEligibleMonthsByYearInfo();
            getChartAccountsInfo();
            getParticipantActualsStatusInfo();

        }
    }, [participantId, forecastYear, expandAll]);



    useEffect(() => {
        if (participantId && currentYear) {
            getBudgetForecastEligibleYearInfo();
        }
    }, [participantId, currentYear]);

    async function getBudgetForecastEligibleYearInfo() {
        try {
            const responseList = await getBudgetForecastEligibleYearByParticipantIdAndYear(participantId, currentYear, userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                setReadOnlyForecast(responseList?.data?.data?.readOnly);
            } else {
                setReadOnlyForecast(false);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        }
    }

    async function getBudgetActualEligibleMonthsByYearInfo() {
        setLoadingFlag(true);
        try {
            const responseList = await getBudgetActualEligibleMonthsByYear(participantId, forecastYear, userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS && responseList.data.data.eligibleYearAndMonthsResponseList[0].eligibleMonthsList.length > 0) {
                setEligibleMonthsVO(responseList.data.data.eligibleYearAndMonthsResponseList[0].eligibleMonthsList.length);
            } else {
                setEligibleMonthsVO([]);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setLoadingFlag(false);
        }
    }
    async function getChartAccountsInfo() {
        setIsLoading(true);
        try {
            const responseList = await getBudgetActualDetailsChartAccountsByYearAndParticipantIdURL(forecastYear, participantId, userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS && responseList.data.data.forecastChartAccountsVOList) {
                setTockenId(responseList.data.data.tockenId)
                const chartAccountsVOList = responseList.data.data.forecastChartAccountsVOList;
                setChartAccountsVOList(responseList.data.data.forecastChartAccountsVOList);
                if (chartAccountsVOList?.length > 0) {
                    const filteredChartAssetAccounts = chartAccountsVOList.filter((income: { accountType: string; }) => income.accountType === "asset");
                    const filteredChartLiabilitiesAccounts = chartAccountsVOList.filter((expenses: { accountType: string; }) => expenses.accountType === "liabilities");
                    const filteredChartNetAssetAccounts = chartAccountsVOList.filter((income: { accountType: string; }) => income.accountType === "net_assets");
                    const filteredChartIncomeAccounts = chartAccountsVOList.filter((income: { accountType: string; }) => income.accountType === "income");
                    const filteredChartExpensesAccounts = chartAccountsVOList.filter((expenses: { accountType: string; }) => expenses.accountType === "expenses");
                    setChartAssetAccountsList(filteredChartAssetAccounts);
                    setChartLiabilitiesAccountsList(filteredChartLiabilitiesAccounts);
                    setChartIncomeAccountsList(filteredChartIncomeAccounts);
                    setChartNetAssetAccountsList(filteredChartNetAssetAccounts);
                    setChartExpensesAccountsList(filteredChartExpensesAccounts);

                    if (chartAccountsVOList) {
                        let incomeTotal = 0;
                        let expensesTotal = 0;
                        let assetTotal = 0;
                        let liabilitiesTotal = 0;
                        let netAssetTotal = 0;

                        for (const account of chartAccountsVOList) {
                            const amount = Number(account.curActualAmount) || 0;

                            switch (account.accountType) {
                                case 'income':
                                    incomeTotal += amount;
                                    break;
                                case 'expenses':
                                    expensesTotal += amount;
                                    break;
                                case 'asset':
                                    assetTotal += amount;
                                    break;
                                case 'liabilities':
                                    liabilitiesTotal += amount;
                                    break;
                                case 'net_assets':
                                    netAssetTotal += amount;
                                    break;
                                default:
                                    break;
                            }
                        }
                        setTotalIncome(incomeTotal);
                        settotalExpenses(expensesTotal);
                        settotalAasset(assetTotal);
                        settotalLiabilities(liabilitiesTotal);
                        settotalNetAsset(netAssetTotal);
                    }


                } else {
                    setChartIncomeAccountsList([]);
                    setChartAssetAccountsList([]);
                    setChartLiabilitiesAccountsList([]);
                    setChartNetAssetAccountsList([]);
                    setChartExpensesAccountsList([]);
                }
            } else {
                setChartIncomeAccountsList([]);
                setChartAssetAccountsList([]);
                setChartLiabilitiesAccountsList([]);
                setChartNetAssetAccountsList([]);
                setChartExpensesAccountsList([]);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setIsLoading(false);
        }
    }


    const transformedChartLiabilitiesAccountsList = chartLiabilitiesAccountsList.map((item: any) => ({
        ...item,
        accountNumber: item.accountNumber < 10000 ? item.accountNumber : '',
        curActualAmount: item.curActualAmount ? formatCurrency(item.curActualAmount) : "$0.00",
        curBudget: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        lastYearTotal: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        incDec: item.curBudget && item.curActualAmount ? formatCurrency(item.curBudget - item.curActualAmount) : "$0.00",
        percentage: item.curBudget && item.curActualAmount ? `${(((item.curBudget - item.curActualAmount) / item.curActualAmount) * 100).toFixed(2)}%` : "-",
    }));
    const transformedChartAssetAccountsList = chartAssetAccountsList.map((item: any) => ({
        ...item,
        accountNumber: item.accountNumber < 10000 ? item.accountNumber : '',
        curActualAmount: item.curActualAmount ? formatCurrency(item.curActualAmount) : "$0.00",
        curBudget: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        lastYearTotal: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        incDec: item.curBudget && item.curActualAmount ? formatCurrency(item.curBudget - item.curActualAmount) : "$0.00",
        percentage: item.curBudget && item.curActualAmount ? `${(((item.curBudget - item.curActualAmount) / item.curActualAmount) * 100).toFixed(2)}%` : "-",
    }));
    const transformedChartNetAssetAccountsList = chartNetAssetAccountsList.map((item: any) => ({
        ...item,
        accountNumber: item.accountNumber < 10000 ? item.accountNumber : '',
        curActualAmount: item.curActualAmount ? formatCurrency(item.curActualAmount) : "$0.00",
        curBudget: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        lastYearTotal: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        incDec: item.curBudget && item.curActualAmount ? formatCurrency(item.curBudget - item.curActualAmount) : "$0.00",
        percentage: item.curBudget && item.curActualAmount ? `${(((item.curBudget - item.curActualAmount) / item.curActualAmount) * 100).toFixed(2)}%` : "-",
    }));
    const transformedChartIncomeAccountsList = chartIncomeAccountsList.map((item: any) => ({
        ...item,
        accountNumber: item.accountNumber < 10000 ? item.accountNumber : '',
        curActualAmount: item.curActualAmount ? formatCurrency(item.curActualAmount) : "$0.00",
        curBudget: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        incDec: item.curBudget && item.curActualAmount ? formatCurrency(item.curBudget - item.curActualAmount) : "$0.00",
        percentage: item.curBudget && item.curActualAmount ? `${(((item.curBudget - item.curActualAmount) / item.curActualAmount) * 100).toFixed(2)}%` : "-",
    }));

    const transformedChartExpensesAccountsList = chartExpensesAccountsList.map((item: any) => ({
        ...item,
        accountNumber: item.accountNumber < 10000 ? item.accountNumber : '',
        curActualAmount: item.curActualAmount ? formatCurrency(item.curActualAmount) : "$0.00",
        curBudget: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
        incDec: item.curBudget && item.curActualAmount ? formatCurrency(item.curBudget - item.curActualAmount) : "$0.00",
        percentage: item.curBudget && item.curActualAmount ? `${(((item.curBudget - item.curActualAmount) / item.curActualAmount) * 100).toFixed(2)}%` : "-",
    }));


    const handleCancel = () => {
        setOpenPopUp(true);
    };
    const handlePopUpClose = () => {
        setOpenPopUp(false);
    };
    function handleConfirm() {
        navigate("/actualsListing");
    }
    const handleBack = () => {
        navigate("/actualsListing");
    };
    const handleReset = () => {
        dispatch(resetSubChartDetails());
        setRefreshKey(prev => prev + 1);
    };

    async function handleSave() {
        const finalRows = subChartSaveVO.subchartArray;

        if (finalRows.length === 0) {
            notifyUser(constants.ERROR_VALIDATION_MESSAGE_NO_VALUE_HAVE_BEEN_MODIFIED, 'error', translate('actuals'));
            return;
        }
        setLoadingFlag(true);
        try {
            const tempList = finalRows.map((updatedSubChart) => {
                const parentChart = chartAccountsVOList.find(
                    (chart) => chart.id === updatedSubChart.forecastChartId
                );
                if (!parentChart) return null;

                return {
                    forecastSubChartId: updatedSubChart.id,
                    forecastChartMonth: updatedSubChart.subChartAccountsTransactions.map((txn: any) => ({
                        id: txn.id,
                        month: txn.month,
                        actualAmount: txn.actualAmount,
                        forecastAmount: txn.forecastAmount,
                    })),
                    subChartAccountsNotesVO: {
                        id: updatedSubChart.subChartAccountsNotesVO?.id ?? 0,
                        forecastSubChartId: updatedSubChart.id,
                        version: updatedSubChart.subChartAccountsNotesVO?.version ?? 0,
                        budgetNotes: updatedSubChart.subChartAccountsNotesVO?.budgetNotes ?? "",
                        actualNotes: updatedSubChart.subChartAccountsNotesVO?.actualNotes ?? "",
                    },
                };
            });

            const updatedForecastChartIdVOList: ForecastChartIdVO[] = tempList.filter((item): item is ForecastChartIdVO => item !== null);
            const actualsChartIdReq: ActualsChartIdReq = {
                forecastChartIdVOList: updatedForecastChartIdVOList,
                tockenId: tockenId,
                userName: userDetails.emailId,
                forecastYear: forecastYear,
                participantId: participantId,
            };
            const responseList = await postActualsChartAccountsTransactions(actualsChartIdReq);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                notifyUser(responseList?.data?.message, 'success', translate('actuals'));
                handleReset();
                getChartAccountsInfo();
                if (expandAll) setExpandAll(false);
            } else {
                notifyUser(responseList?.data?.message, 'error', translate('actuals'));
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setLoadingFlag(false);
        }
    };
    async function handleMarkAsCompleted() {
        setLoadingFlag(true);
        try {
            const responseList = await updateStatusAndActualSubmissionDateByParticipantIdAndFinancialYearAndMonth('Submitted', participantId, forecastYear, "All");
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                setMarkasReadFlag(true);
                notifyUser(responseList?.data?.message, 'success', translate('actuals'));
            } else {
                notifyUser(responseList?.data?.message, 'error', translate('budget'));
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setLoadingFlag(false);
        }
    };

    const handleCollapseAll = () => {
        setLoadingFlag(true);
        setTimeout(() => {
            setExpandAll(prev => !prev);
            setLoadingFlag(false);
        }, 3000);
    };
    const handleDownloadPopUpClose = () => {
        setOpenDownloadPopUp(false);
    };

    function handleDownloadClose() {
        setOpenDownloadPopUp(false);
    }
    function handleConfirmDownload() {
        handleDownloadPopUpClose();
    }

    async function toPrint() {
        const printWindow = window.open("", "_blank", "width=900,height=700");
        if (!printWindow) {
            alert("Popup blocked! Please allow popups for this site.");
            return;
        }

        printWindow.document.write(`
            <html><head><title>Loading...</title></head>
            <body><h2>Actuals Details data...</h2></body>
            </html>
        `);

        openPrintWindow(chartAccountsVOList, printWindow, forecastYear, participantProfile?.participantAdminProfile, columns);
    };

    const openPrintWindow = (tableData: any[], printWindow: Window | null, forecastYear: string, participantName: string, columns: Column[]) => {
        if (!printWindow) return;

        const hasData = Array.isArray(tableData) && tableData.length > 0;

        const generateSubChartRows = (subCharts: any[]) => {
            let totalPrevBudget = 0;
            let totalCurBudget = 0;
            let totalIncDec = 0;

            const rows = subCharts.map((sub, subIdx) => {
                const subAccountNumber = String(sub.accountNumber).includes('-') || sub.accountNumber < 10000 ? sub.accountNumber : '';
                const subDesc = sub.accountName ?? '';
                const subActualAmount = sub.fullBudget ? formatCurrencywithoutDoller(sub.fullBudget) : ' 0.00';
                const subBudget = sub.fullActualAmount ? formatCurrencywithoutDoller(sub.fullActualAmount) : ' 0.00';
                const subIncDec = sub.fullBudget && sub.fullActualAmount ? formatCurrencywithoutDoller(sub.fullBudget - sub.fullActualAmount) : ' 0.00';
                const subPercentage = sub.fullBudget && sub.fullActualAmount ? `${(((sub.fullActualAmount - sub.fullBudget) / sub.fullBudget) * 100).toFixed(2)}` : '-';

                const prev = sub.fullBudget ?? 0;
                const cur = sub.fullActualAmount ?? 0;
                const diff = cur - prev;

                totalPrevBudget += prev;
                totalCurBudget += cur;
                totalIncDec += diff;
                return `
                    <tr style="background-color: ${subIdx % 2 === 0 ? '#fafafa' : '#fff'};">
                        <td style="text-align:center;">${subAccountNumber}</td>
                        <td style="text-align:left;">&nbsp;&nbsp;&nbsp;${subDesc}</td>
                        <td style="text-align:right;">${subActualAmount}</td>
                        <td style="text-align:right;">${subBudget}</td> 
                        <td style="text-align:right;">${subIncDec}</td>
                        <td style="text-align:right;">${subPercentage}</td>
                    </tr>
                `;
            }).join('');
            const formattedTotalPrev = formatCurrencywithoutDoller(totalPrevBudget);
            const formattedTotalCur = formatCurrencywithoutDoller(totalCurBudget);
            const formattedTotalIncDec = formatCurrencywithoutDoller(totalIncDec);
            const totalPercentage = totalPrevBudget ? ((totalIncDec / totalPrevBudget) * 100).toFixed(2) : '-';


            const totalRow = `
                <tr style="background-color: #eaeaea; font-weight: bold;">
                    <td style="text-align:center;"></td>
                    <td style="text-align:right;"> Total</td>
                    <td style="text-align:right;">${formattedTotalPrev}</td>
                    <td style="text-align:right;">${formattedTotalCur}</td>
                    <td style="text-align:right;">${formattedTotalIncDec}</td>
                    <td style="text-align:right;">${totalPercentage}</td> 
                </tr>
            `;

            return rows + totalRow;
        };

        const tableRows = hasData
            ? tableData.map((row, index) => {
                const accountNumber = row.accountNumber < 10000 ? row.accountNumber : '';
                const desc = row.accountName ?? '';

                const subChartRows = row.forecastSubChartAccountsList?.length ? generateSubChartRows(row.forecastSubChartAccountsList) : '';

                return `
                   <tr style="background-color: ${index % 2 === 0 ? '#f1f3f4' : '#fff'};">
                    <td style="text-align:center; font-weight: 600;">${accountNumber}</td>
                    <td style="text-align:left; font-weight: 600;">${desc}</td> 
                    <td style="text-align:left; font-weight: 600;"> </td> 
                    <td style="text-align:left; font-weight: 600;"> </td> 
                    <td style="text-align:left; font-weight: 600;"> </td> 
                    <td style="text-align:left; font-weight: 600;"> </td> 
                </tr>
                    ${subChartRows}
                `;
            }).join('')
            : `
                <tr>
                    <td colspan="6" align="center" sx={{ py: 1 }}>
                        No records found
                    </td>
                </tr>
            `;

        const tableHeaders = columns
            .filter(column => column.label !== "Last Year YTD Total")
            .map((column) => {
                let label = column.label;
                if (label === "Charts Account No") {
                    label = "Account No";
                } else if (["Increase/Decr.", "Actuals Amount"].includes(label) || label.includes("Budget")) {
                    label = "$";
                }
                return `
                <th style="text-align: ${column.align}; padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5;">
                    ${label}
                </th>
            `;
            }).join('');
        const budgetColumns = columns.filter(col => col.label.includes("Budget"));
        const hasIncrease = columns.some(col => col.label === "Increase/Decr.");
        const hasVariance = columns.some(col => col.label === "%");

        // Exclude "Actuals Amount" and "Last Year YTD Total" from columns
        const filteredColumns = columns.filter(col => col.label !== "Actuals Amount" && col.label !== "Last Year YTD Total" && col.label !== "Budget Amount");

        const additionalHeaders = `
                ${hasIncrease ? `<th style="text-align:center; background-color: #f5f5f5; border: 1px solid #ddd; font-weight: bold;">Increase/Decr.</th>` : ''}
                ${hasVariance ? `<th style="text-align:center; background-color: #f5f5f5; border: 1px solid #ddd; font-weight: bold;">Variance</th>` : ''}
            `;

        // Add 2 more <th> elements for the additional budget periods
        const additionalBudgetHeaders = `
                <th style="text-align: right; padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5;">
                    ${translate('budget')} (${currentYear - 1} - ${currentYear})
                </th>
                <th style="text-align: right; padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5;">
                    ${translate('actuals')} (${currentYear - 1} - ${currentYear})
                </th>
            `;

        const combinedGroupHeaderRow = (budgetColumns.length > 0 || filteredColumns.length > 0)
            ? ` <tr> 
            <th style="text-align:left; font-weight: 600;"></th>
            <th style="text-align:left; font-weight: 600;"></th> 
            ${additionalBudgetHeaders}
            ${additionalHeaders}
        </tr>  `
            : '';

        const tableHtml = `
            <html>
            <head>
                <title>Actuals ${forecastYear} - ${participantName}</title>
                  <h1 style=" text-align: center;"> ${participantName}</h1>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 20px;
                    }
                    table {
                        border-collapse: collapse;
                        width: 100%;
                        margin-top: 20px;
                    }
                    th, td {
                        padding: 8px;
                        border: 1px solid #ddd;
                    }
                    th {
                        background-color: #f5f5f5;
                    }
                    tr:nth-child(even) {
                        background-color: #f9f9f9;
                    }
                </style>
            </head>
            <body>
                <table>
                <thead>
                        ${combinedGroupHeaderRow}
                        <tr>
                            ${tableHeaders}
                        </tr>
                    </thead> 
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
                <script>
                    window.onload = function() {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(tableHtml);
        printWindow.document.close();
    };

    const handleDownload = () => {
        setOpenDownloadPopUp(true);
    };

    async function getParticipantActualsStatusInfo() {
        try {
            const responseList = await getParticipantActualsStatusByParticipantIdAndFinancialYearAndMonth(participantId, forecastYear, "All", userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                if (responseList?.data.data.status == constants.RESPONSE_SUBMITTED) {
                    setMarkasReadFlag(true);
                } else {

                    setMarkasReadFlag(false);
                }
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
        }
    }
    const handleFinalDataUpdate = (finalUpdatedData: any[]) => {
        setSaveUpdatedChartAccountsList(finalUpdatedData)
    };
    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('actuals')} {forecastYear}</SectionTitle>
                    <Box sx={{ mx: 2 }}>
                        <Box className={basestyle.buttoncontainer} sx={{ mt: -1 }}>
                            <ContainedButton onClick={handleCollapseAll}> {expandAll ? translate('collapseAll') : translate('expandAll')}</ContainedButton>
                            <ContainedButton onClick={handleDownload}  >{translate('download')}</ContainedButton>
                            <ContainedButton onClick={toPrint} >{translate('print')}</ContainedButton>
                        </Box>

                        <SubSectionTitle  >{translate('assetSummary')}</SubSectionTitle>
                        <ReusableChartsAccountTable
                            tableKey="chartAssetAccountsListing"
                            columns={filterColumns("chartAssetAccountsListing")}
                            rows={transformedChartAssetAccountsList}
                            loading={isLoading}
                            readOnlyForecast={readOnlyForecast}
                            forecastYear={forecastYear}
                            totalAmount={totalAsset}
                            isExpand={expandAll}
                            pageType="ActualsDetails"
                            onDataChange={handleFinalDataUpdate}
                            refreshKey={refreshKey}
                        />

                        <SubSectionTitle  >{translate('liabilitiesSummary')}</SubSectionTitle>
                        <ReusableChartsAccountTable
                            tableKey="chartLiabilitiesAccountsListing"
                            columns={filterColumns("chartLiabilitiesAccountsListing")}
                            rows={transformedChartLiabilitiesAccountsList}
                            loading={isLoading}
                            readOnlyForecast={readOnlyForecast}
                            forecastYear={forecastYear}
                            totalAmount={totalLiabilities}
                            isExpand={expandAll}
                            pageType="ActualsDetails"
                            onDataChange={handleFinalDataUpdate}
                            refreshKey={refreshKey}
                        />

                        <SubSectionTitle  >{translate('netAssetsSummary')}</SubSectionTitle>
                        <ReusableChartsAccountTable
                            tableKey="chartNetAssetsAccountsListing"
                            columns={filterColumns("chartNetAssetsAccountsListing")}
                            rows={transformedChartNetAssetAccountsList}
                            loading={isLoading}
                            readOnlyForecast={readOnlyForecast}
                            forecastYear={forecastYear}
                            totalAmount={totalNetAsset}
                            isExpand={expandAll}
                            pageType="ActualsDetails"
                            onDataChange={handleFinalDataUpdate}
                            refreshKey={refreshKey}
                        />

                        <SubSectionTitle  >{translate('incomeSummary')}</SubSectionTitle>
                        <ReusableChartsAccountTable
                            tableKey="chartActualsIncomeAccountsListing"
                            columns={filterColumns("chartActualsIncomeAccountsListing")}
                            rows={transformedChartIncomeAccountsList}
                            loading={isLoading}
                            readOnlyForecast={readOnlyForecast}
                            forecastYear={forecastYear}
                            totalAmount={totalIncome}
                            isExpand={expandAll}
                            pageType="ActualsDetails"
                            onDataChange={handleFinalDataUpdate}
                            refreshKey={refreshKey}
                        />

                        <SubSectionTitle  >{translate('expensesSummary')}</SubSectionTitle>
                        <ReusableChartsAccountTable
                            tableKey="chartActualsExpensesAccountsListing"
                            columns={filterColumns("chartActualsExpensesAccountsListing")}
                            rows={transformedChartExpensesAccountsList}
                            loading={isLoading}
                            readOnlyForecast={readOnlyForecast}
                            forecastYear={forecastYear}
                            totalAmount={totalExpenses}
                            isExpand={expandAll}
                            pageType="ActualsDetails"
                            onDataChange={handleFinalDataUpdate}
                            refreshKey={refreshKey}
                        />

                        <Box className={basestyle.buttoncontainer} >
                            <ContainedButton onClick={handleBack}> {translate('back')}  </ContainedButton>
                            <ContainedButton onClick={handleSave}  > {translate('save')}  </ContainedButton>
                            <ContainedButton onClick={handleMarkAsCompleted} disabled={markasReadFlag}>{translate('markAsCompleted')}</ContainedButton>
                            <OutlinedButton onClick={handleCancel}  > {translate('cancel')}  </OutlinedButton>
                        </Box>
                    </Box>
                    <CancelPopUp
                        open={openPopUp}
                        popupTitle={translate('confirmCancel')}
                        handleClose={handlePopUpClose}
                        saveChanges={() => handleConfirm()}
                        message={translate('doYouWantToCancelChanges?')}
                    />
                    <DownloadPopUpActuals
                        open={openDownloadPopUp}
                        forecastYear={forecastYear}
                        participantId={participantId}
                        handleClose={handleDownloadClose}
                        saveChanges={() => handleConfirmDownload()}
                    />
                    <Backdrop
                        sx={{
                            color: "#fff",
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                            display: "flex",
                            flexDirection: "column",
                        }}
                        open={loadingFlag}
                    >
                        <CircularProgress color="inherit" />
                        <SubSectionTitle  >{translate('pleasewait')}</SubSectionTitle>
                    </Backdrop>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    );
};

export default ActualsDetails;